import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { AnalysisResults } from './components/AnalysisResults';
import { ScanningScreen } from './components/ScanningScreen';
import { ErrorMessage } from './components/ErrorMessage';
import { apiService } from './services/api';
import { AnalysisResult } from './types/analysis';
import { Upload } from 'lucide-react';
import { FeedbackForm } from './components/FeedbackForm';
import { AdminPanel } from './pages/AdminPanel';
import { analyticsEvents } from './services/vercelAnalytics';

function App() {
  const [screen, setScreen] = useState<'home' | 'scanning' | 'results'>('home');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check for admin route and track app load
  React.useEffect(() => {
    analyticsEvents.appLoaded();
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === 'true') {
      setShowAdmin(true);
      analyticsEvents.adminPanelAccessed();
    }
  }, []);


  const handleFileSelect = async (file: File) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const previewUrl = reader.result as string;
        setSelectedFile(file);
        setPreview(previewUrl);
        setResult(null);
        setError(null);
        setScreen('scanning');

        // Track image upload
        analyticsEvents.imageUploaded(file.size, file.type);
        analyticsEvents.analysisStarted();

        try {
          const analysisResult = await apiService.analyzeImage(file);
          setResult(analysisResult);
          setScreen('results');
          
          // Track successful analysis
          analyticsEvents.analysisCompleted(
            analysisResult.source,
            analysisResult.ai_probability || 0,
            analysisResult.risk_score || 0
          );
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Analysis failed';
          setError(errorMsg);
          setScreen('home');
          
          // Track failed analysis
          analyticsEvents.analysisFailed(errorMsg);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setScreen('home');
  };

  const handleRetry = () => {
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  // Show admin panel if requested
  if (showAdmin) {
    return <AdminPanel />;
  }

  return (
    <div className="min-h-screen bg-gray-300/20 pb-24 relative overflow-hidden">
      {/* Pattern Background */}
      <div className="fixed inset-0 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E5EDFF" strokeWidth="0.5" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10">
      {/* Header */}
      <header className="bg-gray-200/80 backdrop-blur-sm border-b border-[#D0D5DD] sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <h1 className="text-2xl font-bold text-[#0A1A2F]">nomorai</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Home Screen */}
        {screen === 'home' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            {/* AI Images Detected Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="bg-white border border-[#D0D5DD] rounded-3xl p-6 space-y-4 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-[#0A1A2F]">AI Images Detected</h3>
                  <p className="text-sm text-[#475467] mt-2">
                    Advanced metadata analysis helps identify AI-generated images with high accuracy. Our system checks for digital fingerprints and anomalies.
                  </p>
                </div>
                <div className="text-3xl font-bold text-[#1F4FFF]">10</div>
              </div>
              
            </motion.div>

            {/* Feedback & Facts Section - Flexbox Row */}
            <div className="flex flex-row md:flex-row gap-4">
              {/* Feedback Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="flex-1 bg-white border border-[#D0D5DD] rounded-2xl p-5 shadow-sm"
              >
                <h4 className="font-semibold">Share Feedback</h4>
                <p className="text-sm text-[#475467] mb-4">Help us improve detection accuracy</p>
                <button 
                  onClick={() => setShowFeedbackForm(true)}
                  className="w-full px-2 py-2 text-sm text-[#475467] bg-white/30 backdrop-blur-xl border border-black/10 font-medium rounded-2xl hover:bg-white/40 transition-all shadow-lg mt-10"
                >
                  Send Feedback
                </button>
              </motion.div>

              {/* Interesting Fact Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="flex-1 bg-white border border-[#D0D5DD] rounded-2xl p-5 shadow-sm"
              >
                <h4 className="font-semibold text-[#0A1A2F] mb-3">Did You Know?</h4>
                <p className="text-sm text-[#475467]">
                  Over 90% of deepfakes can be detected using advanced digital forensics techniques.
                </p>
              </motion.div>
            </div>

            {error && (
              <ErrorMessage 
                message={error} 
                onRetry={handleRetry}
              />
            )}
          </motion.div>
        )}

        {/* Scanning Screen */}
        {screen === 'scanning' && preview && (
          <ScanningScreen preview={preview} />
        )}

        {/* Results Screen */}
        {screen === 'results' && result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            <AnalysisResults result={result} preview={preview || undefined} />
            
            <div className="text-center pb-8 ml-5">
              <button
                onClick={handleReset}
                className="w-full max-w-xs px-8 py-4 text-[#475467] bg-white/30 backdrop-blur-xl border border-black/10 font-medium rounded-3xl hover:bg-white/40 transition-all shadow-lg flex items-center justify-center space-x-2"
              >
                <Upload className="w-5 h-5" />
                <span>Analyze Another Image</span>
              </button>
            </div>
          </motion.div>
        )}
      </main>

      {/* Bottom Navbar - Glassmorphic - Only on Home */}
      {screen === 'home' && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-3"
            >
              <button
                onClick={handleUploadClick}
                className="w-full px-6 py-4 text-[#475467] bg-white/30 backdrop-blur-xl border border-black/10 font-medium rounded-2xl hover:bg-white/40 transition-all shadow-lg"
              >
                Click to Upload Image
              </button>
              <p className="text-xs text-center text-[#475467]">
                JPEG, PNG • Up to 10MB
              </p>
            </motion.div>
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Feedback Form Modal */}
      <FeedbackForm 
        isOpen={showFeedbackForm} 
        onClose={() => setShowFeedbackForm(false)} 
      />
      </div>
    </div>
  );
}

export default App;
